import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { lazyInject } from '@app/containers/base/decorators';
import {
  IBaseEntityType,
  IGridFlatInventoryItemVenue,
} from '@app/shared/types';
import { flatTreeDataByHasChildren } from '@app/shared/utils';
import { ENTITY_ID_PATH } from '@config/global-constants';
import { concat, get, includes, isFunction, map, set, merge } from 'lodash';
import { ORDER_STATUS_VALUE } from '@app/shared/types/enums/order-status.enum';
import { OrderTotalFormService } from './order-totals-form.service';
import {
  PdfGenerateServiceV2,
  ViewSupportType,
} from '@app/shared/common/make-pdf/v2';
import { OrdersEditContainerComponent } from '@app/containers/orders/containers';
import { ORDER_FORM_FIELD_PATHS } from './order-form-field-paths';
import { sortAndFilterData } from '@app/shared/common/make-pdf/v2/utils';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { buildOrderPdfConfig } from './order-pdf-config';

@Injectable()
export class OrderPdfService {
  @lazyInject(OrderTotalFormService)
  public totalFromService: OrderTotalFormService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public getHeaderText(status: string): string {
    return this.isOpenOrCanceled(status) ? 'Order Docket' : 'Delivery Docket';
  }

  public async handlePdfClick(
    editor: OrdersEditContainerComponent,
    action: 'print' | 'export',
  ): Promise<void> {
    const isOpenOrCanceled = this.isOpenOrCanceled(editor.form.value.status);
    const pdfConfig = buildOrderPdfConfig(
      async () =>
        this.getPdfData(
          merge(
            { internal_number: editor.document?.internal_number },
            editor.form.value,
          ),
          editor.entityFormService.xuxGrid,
        ),
      !isOpenOrCanceled,
    );

    const actionMapping = {
      print: this.pdfGenerateServiceV2.printPdfAsync.bind(
        this.pdfGenerateServiceV2,
      ),
      export: this.pdfGenerateServiceV2.downloadPdfAsync.bind(
        this.pdfGenerateServiceV2,
      ),
    };

    await actionMapping[action](pdfConfig);
  }

  public async getPdfData(
    doc: IBaseEntityType,
    xuxGrid: XuxEditGridComponent,
    isFromDetail = true,
  ): Promise<IBaseEntityType> {
    const gridFilterString = get(
      xuxGrid,
      'xuxGridComponent.actionPanel.quickFilterControl.value',
    );

    const status = doc[ORDER_FORM_FIELD_PATHS.status];

    const useIdStatus = includes(
      [
        ORDER_STATUS_VALUE.Pending,
        ORDER_STATUS_VALUE.Completed,
        ORDER_STATUS_VALUE.Rejected,
        ORDER_STATUS_VALUE.Canceled,
      ],
      status,
    );

    const inventoryItemData =
      flatTreeDataByHasChildren<IGridFlatInventoryItemVenue>(
        get(doc, ORDER_FORM_FIELD_PATHS.inventoryItemVenues),
      );

    const valuePath = useIdStatus ? '_id' : ENTITY_ID_PATH;

    const filteredData: IGridFlatInventoryItemVenue[] = isFromDetail
      ? sortAndFilterData(
          inventoryItemData,
          {
            baseOnGrid: xuxGrid,
            viewSupport: ViewSupportType.sortAndFilter,
          },
          valuePath,
          valuePath,
        )
      : get(doc, ORDER_FORM_FIELD_PATHS.inventoryItemVenues);

    const document: IBaseEntityType = {
      gridFilterString,
      invoice_number: doc.invoice_number,
      internal_number: doc.internal_number,
      isOpenOrCanceledStatus: this.isOpenOrCanceled(status),
      [ORDER_FORM_FIELD_PATHS.status]: doc[ORDER_FORM_FIELD_PATHS.status],
      [ORDER_FORM_FIELD_PATHS.vendorName]:
        doc[ORDER_FORM_FIELD_PATHS.vendorName],
      [ORDER_FORM_FIELD_PATHS.orderDate]: doc[ORDER_FORM_FIELD_PATHS.orderDate],
      [ORDER_FORM_FIELD_PATHS.deliveryDate]:
        doc[ORDER_FORM_FIELD_PATHS.deliveryDate],
      external_order_number: doc.external_order_number,
      [ORDER_FORM_FIELD_PATHS.siteName]: doc[ORDER_FORM_FIELD_PATHS.siteName],
      [ORDER_FORM_FIELD_PATHS.storeNumber]:
        doc[ORDER_FORM_FIELD_PATHS.storeNumber],
      virtual: {},
    };

    const totals = this.totalFromService.mapItemDataToTotalsData(
      inventoryItemData,
      ORDER_FORM_FIELD_PATHS,
    );

    const pinnedBottomData = map(totals.pinnedBottomData, (bottomData) => ({
      ...bottomData,
      isPinnedBottom: true,
    }));

    const getTotalCostShowInDetailPage = (): string =>
      document.isOpenOrCanceledStatus
        ? totals.pinnedBottomData?.[0]?.total_cost?.toString()
        : totals.pinnedBottomData?.[0]?.received_total_cost?.toString();

    set(
      document,
      ORDER_FORM_FIELD_PATHS.virtualInventoryItemVenues,
      filteredData,
    );
    set(
      document,
      ORDER_FORM_FIELD_PATHS.virtualTotalStatus,
      concat(totals.normalRows, pinnedBottomData),
    );
    set(document, 'virtual.grand_totals', {
      tax_fee: get(doc, 'tax_fee'),
      delivery_fee: get(doc, 'delivery_fee'),
      grand_total: isFromDetail
        ? get(doc, 'virtual.grand_total')
        : get(doc, 'total_cost'),
      total_cost_show: isFromDetail
        ? get(doc, 'virtual.total_cost_show')
        : getTotalCostShowInDetailPage(),
    });

    return document;
  }

  public isOpenOrCanceled(status: string): boolean {
    return includes(
      [ORDER_STATUS_VALUE.Canceled, ORDER_STATUS_VALUE.Open],
      status,
    );
  }
}
